import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Card } from '../../components/ui/Card';
import { AlertCircle, CreditCard, Shield, Check, QrCode, FileText, Copy, Clock } from 'lucide-react';
import { getOnboardingData, getPlanById } from '../../services/storage';

type PaymentMethod = 'credit_card' | 'pix' | 'boleto';

export const Payment = () => {
  const navigate = useNavigate();
  const { completePayment } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('credit_card');
  const [pixCopied, setPixCopied] = useState(false);

  const [formData, setFormData] = useState({
    cardNumber: '',
    cardHolder: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: '',
    cpf: ''
  });

  const onboardingData = getOnboardingData();
  const plan = onboardingData?.planId ? getPlanById(onboardingData.planId) : null;
  const billingCycle = onboardingData?.billingCycle || 'monthly';

  if (!plan) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-light">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-error mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-neutral-900 mb-2">Plano não encontrado</h2>
          <p className="text-neutral-600 mb-6">Volte e selecione um plano válido.</p>
          <Link to="/onboarding/plano">
            <Button variant="primary">Voltar para planos</Button>
          </Link>
        </div>
      </div>
    );
  }

  const formatCardNumber = (value: string): string => {
    const cleaned = value.replace(/\D/g, '');
    return cleaned.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
  };

  const formatCPF = (value: string): string => {
    const cleaned = value.replace(/\D/g, '');
    return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{0,2})/, '$1.$2.$3-$4');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    let formattedValue = value;
    if (name === 'cardNumber') {
      formattedValue = formatCardNumber(value);
    } else if (name === 'cpf') {
      formattedValue = formatCPF(value);
    }
    
    setFormData(prev => ({ ...prev, [name]: formattedValue }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const result = await completePayment({ ...formData, method: paymentMethod });
      if (result.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      } else {
        setError(result.error || 'Erro ao processar pagamento');
      }
    } catch (err) {
      setError('Erro ao processar pagamento. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const copyPixCode = () => {
    const pixCode = '00020126580014br.gov.bcb.pix0136portal.ethics@email.com5204000053039865405970.005802BR5913Portal Ethics6009Sao Paulo62140510PortalEthics6304';
    navigator.clipboard.writeText(pixCode);
    setPixCopied(true);
    setTimeout(() => setPixCopied(false), 3000);
  };

  const amount = billingCycle === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice;

  if (success) {
    return (
      <>
        <Helmet>
          <title>Pagamento Confirmado | Portal Ethics</title>
        </Helmet>
        <div className="min-h-screen flex items-center justify-center bg-background-light py-12 px-4">
          <div className="max-w-md w-full text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-3xl font-extrabold text-neutral-900 mb-4">
              Pagamento confirmado!
            </h2>
            <p className="text-neutral-600 mb-8">
              Sua assinatura foi ativada com sucesso. Redirecionando para o dashboard...
            </p>
            <div className="animate-pulse">
              <div className="h-2 bg-primary-blue rounded-full w-32 mx-auto"></div>
            </div>
          </div>
        </div>
      </>
    );
  }

  const paymentMethods = [
    { id: 'credit_card' as PaymentMethod, label: 'Cartão de Crédito', icon: CreditCard },
    { id: 'pix' as PaymentMethod, label: 'PIX', icon: QrCode },
    { id: 'boleto' as PaymentMethod, label: 'Boleto Bancário', icon: FileText },
  ];

  return (
    <>
      <Helmet>
        <title>Pagamento | Portal Ethics</title>
      </Helmet>
      
      <div className="min-h-screen bg-background-light py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Progress Steps */}
          <div className="mb-8">
            <div className="flex items-center justify-center">
              <div className="flex items-center">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-green-500 text-white font-semibold">
                  <Check className="w-5 h-5" />
                </div>
                <div className="ml-4 text-sm font-medium text-neutral-900">Cadastro</div>
              </div>
              <div className="w-24 h-1 bg-green-500 mx-4"></div>
              <div className="flex items-center">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-green-500 text-white font-semibold">
                  <Check className="w-5 h-5" />
                </div>
                <div className="ml-4 text-sm font-medium text-neutral-900">Plano</div>
              </div>
              <div className="w-24 h-1 bg-primary-blue mx-4"></div>
              <div className="flex items-center">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary-blue text-white font-semibold">
                  3
                </div>
                <div className="ml-4 text-sm font-medium text-neutral-900">Pagamento</div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Payment Form */}
            <div>
              <h2 className="text-2xl font-extrabold text-neutral-900 mb-6">
                Dados do pagamento
              </h2>

              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3 text-red-700">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              {/* Payment Method Selector */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-neutral-700 mb-3">
                  Forma de pagamento
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {paymentMethods.map((method) => {
                    const Icon = method.icon;
                    return (
                      <button
                        key={method.id}
                        type="button"
                        onClick={() => setPaymentMethod(method.id)}
                        className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                          paymentMethod === method.id
                            ? 'border-primary-blue bg-primary-blue/5'
                            : 'border-neutral-200 hover:border-neutral-300'
                        }`}
                      >
                        <Icon className={`w-6 h-6 ${paymentMethod === method.id ? 'text-primary-blue' : 'text-neutral-500'}`} />
                        <span className={`text-xs font-medium ${paymentMethod === method.id ? 'text-primary-blue' : 'text-neutral-600'}`}>
                          {method.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {paymentMethod === 'credit_card' && (
                  <div className="space-y-4">
                    <Input
                      label="Número do Cartão"
                      name="cardNumber"
                      type="text"
                      value={formData.cardNumber}
                      onChange={handleChange}
                      placeholder="0000 0000 0000 0000"
                      required
                      maxLength={19}
                      icon={<CreditCard className="w-4 h-4" />}
                    />

                    <Input
                      label="Nome no Cartão"
                      name="cardHolder"
                      type="text"
                      value={formData.cardHolder}
                      onChange={handleChange}
                      placeholder="Como aparece no cartão"
                      required
                    />

                    <div className="grid grid-cols-3 gap-4">
                      <Input
                        label="Mês"
                        name="expiryMonth"
                        type="text"
                        value={formData.expiryMonth}
                        onChange={handleChange}
                        placeholder="MM"
                        required
                        maxLength={2}
                      />
                      <Input
                        label="Ano"
                        name="expiryYear"
                        type="text"
                        value={formData.expiryYear}
                        onChange={handleChange}
                        placeholder="AA"
                        required
                        maxLength={2}
                      />
                      <Input
                        label="CVV"
                        name="cvv"
                        type="text"
                        value={formData.cvv}
                        onChange={handleChange}
                        placeholder="123"
                        required
                        maxLength={4}
                      />
                    </div>

                    <Input
                      label="CPF do Titular"
                      name="cpf"
                      type="text"
                      value={formData.cpf}
                      onChange={handleChange}
                      placeholder="000.000.000-00"
                      required
                      maxLength={14}
                    />
                  </div>
                )}

                {paymentMethod === 'pix' && (
                  <div className="space-y-6">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                      <div className="w-48 h-48 bg-white mx-auto mb-4 rounded-lg flex items-center justify-center border-2 border-dashed border-green-300">
                        <QrCode className="w-32 h-32 text-green-600" />
                      </div>
                      <p className="text-sm text-green-800 font-medium mb-2">
                        Escaneie o QR Code com seu aplicativo bancário
                      </p>
                      <p className="text-xs text-green-600 mb-4">
                        O pagamento será processado instantaneamente
                      </p>
                      
                      <div className="flex items-center gap-2 bg-white p-3 rounded-lg border border-green-200">
                        <code className="flex-1 text-xs text-neutral-600 truncate">
                          portal.ethics@email.com
                        </code>
                        <button
                          type="button"
                          onClick={copyPixCode}
                          className="flex items-center gap-1 text-green-600 hover:text-green-700 text-sm font-medium"
                        >
                          {pixCopied ? (
                            <>
                              <Check className="w-4 h-4" />
                              Copiado!
                            </>
                          ) : (
                            <>
                              <Copy className="w-4 h-4" />
                              Copiar
                            </>
                          )}
                        </button>
                      </div>
                    </div>

                    <div className="flex items-start gap-3 text-sm text-neutral-600 bg-neutral-50 p-4 rounded-lg">
                      <Clock className="w-5 h-5 text-neutral-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-neutral-700">Validade do PIX</p>
                        <p>Este código PIX é válido por 24 horas. Após o pagamento, sua assinatura será ativada automaticamente.</p>
                      </div>
                    </div>
                  </div>
                )}

                {paymentMethod === 'boleto' && (
                  <div className="space-y-6">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <FileText className="w-8 h-8 text-blue-600" />
                        <div>
                          <h3 className="font-semibold text-blue-900">Boleto Bancário</h3>
                          <p className="text-sm text-blue-700">Pagamento processado em até 3 dias úteis</p>
                        </div>
                      </div>

                      <div className="bg-white p-4 rounded-lg border border-blue-200 mb-4">
                        <label className="block text-sm font-medium text-neutral-700 mb-2">
                          Linha Digitável
                        </label>
                        <div className="flex items-center gap-2">
                          <code className="flex-1 text-sm text-neutral-600 font-mono bg-neutral-100 p-2 rounded">
                            34191.79001 01043.510047 91020.150008 8 90010000097000
                          </code>
                          <button
                            type="button"
                            onClick={() => {
                              navigator.clipboard.writeText('34191.79001 01043.510047 91020.150008 8 90010000097000');
                            }}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Copiar linha digitável"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      <div className="flex gap-3">
                        <Button type="button" variant="outline" className="flex-1 text-sm">
                          Visualizar Boleto
                        </Button>
                        <Button type="button" variant="outline" className="flex-1 text-sm">
                          Enviar por Email
                        </Button>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-start gap-3 text-sm text-neutral-600">
                        <Clock className="w-5 h-5 text-neutral-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium text-neutral-700">Vencimento</p>
                          <p>O boleto vence em 3 dias úteis. Após o pagamento, sua assinatura será ativada em até 48 horas.</p>
                        </div>
                      </div>
                    </div>

                    <Input
                      label="CPF/CNPJ do Pagador"
                      name="cpf"
                      type="text"
                      value={formData.cpf}
                      onChange={handleChange}
                      placeholder="000.000.000-00"
                      required
                      maxLength={14}
                    />
                  </div>
                )}

                <div className="flex items-center gap-2 text-sm text-neutral-600">
                  <Shield className="w-4 h-4 text-success" />
                  <span>Pagamento seguro e criptografado</span>
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  className="w-full"
                  disabled={isLoading}
                >
                  {isLoading 
                    ? 'Processando...' 
                    : paymentMethod === 'credit_card' 
                      ? 'Finalizar Pagamento' 
                      : paymentMethod === 'pix'
                        ? 'Confirmar Pagamento PIX'
                        : 'Gerar Boleto'
                  }
                </Button>
              </form>
            </div>

            {/* Order Summary */}
            <div>
              <Card className="p-6 sticky top-8">
                <h3 className="text-lg font-bold text-neutral-900 mb-4">
                  Resumo do pedido
                </h3>

                <div className="space-y-4 mb-6">
                  <div className="flex justify-between items-center">
                    <span className="text-neutral-600">Plano</span>
                    <span className="font-medium text-neutral-900">{plan.name}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-neutral-600">Ciclo</span>
                    <span className="font-medium text-neutral-900">
                      {billingCycle === 'monthly' ? 'Mensal' : 'Anual'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-neutral-600">Usuários</span>
                    <span className="font-medium text-neutral-900">
                      {plan.maxUsers === 999999 ? 'Ilimitados' : `Até ${plan.maxUsers}`}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-neutral-600">Pagamento</span>
                    <span className="font-medium text-neutral-900">
                      {paymentMethod === 'credit_card' && 'Cartão de Crédito'}
                      {paymentMethod === 'pix' && 'PIX'}
                      {paymentMethod === 'boleto' && 'Boleto Bancário'}
                    </span>
                  </div>
                </div>

                <div className="border-t border-neutral-200 pt-4 mb-6">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold text-neutral-900">Total</span>
                    <span className="text-2xl font-extrabold text-primary-blue">
                      {new Intl.NumberFormat('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0
                      }).format(amount)}
                    </span>
                  </div>
                  <p className="text-sm text-neutral-500 mt-1">
                    {billingCycle === 'monthly' ? 'Cobrança mensal' : 'Cobrança anual'}
                  </p>
                </div>

                <div className="bg-neutral-50 p-4 rounded-lg">
                  <h4 className="font-medium text-neutral-900 mb-2">Seu plano inclui:</h4>
                  <ul className="space-y-2">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-neutral-600">
                        <Check className="w-4 h-4 text-success flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Card>
            </div>
          </div>

          <div className="mt-8 text-center">
            <Link to="/onboarding/plano" className="text-sm text-neutral-600 hover:text-primary-blue">
              ← Voltar para seleção de plano
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};
